import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import "./ProjectMasterTable.css";

const ProjectMasterTable = () => {
    const navigate = useNavigate();

    const [locationsList, setLocationsList] = useState([]);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    // 1行 = 1パターン（＝独立した勤務地＋プロジェクト名セット）。
    // タイプ（シフト）はこの画面では編集しないが、保存時に消さないよう patternTypes に保持する。
    const [rows, setRows] = useState([
        {
            id: 1,
            project_name: "",
            work_location_id: "",
            patternTypes: [],   // [{ type_number, shift_id }] 表示はしないが保持
            is_remote_enabled: false,
            remote_type: "NONE",
            remote_frequency_value: "",
        }
    ]);

    // 同一プロジェクト（＝1つの ProjectMaster）にまとめるためのキー
    const groupKey = (r) =>
        r.server_id ? `id:${r.server_id}` : `new:${r.work_location_id}|${r.project_name}`;

    // パターン番号は「行の通し番号」
    const patternNoByRowId = useMemo(() => {
        const map = {};
        rows.forEach((r, i) => { map[r.id] = i + 1; });
        return map;
    }, [rows]);

    // APIのプロジェクト（patterns[].types[]）を画面行（1パターン=1行）へ展開
    const loadRows = async () => {
        try {
            const res = await api.get("/api/admin/projects/");
            const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
            if (list.length > 0) {
                const newRows = [];
                list.forEach((proj) => {
                    const pats = (proj.patterns && proj.patterns.length) ? proj.patterns : [null];
                    pats.forEach((pt, idx) => {
                        const patternTypes = pt
                            ? (pt.types || []).map((t) => ({ type_number: t.type_number, shift_id: t.shift_id }))
                            : [];
                        newRows.push({
                            id: `${proj.id}-${pt ? pt.pattern_number : 0}-${idx}`,
                            server_id: proj.id,
                            project_name: proj.project_name || "",
                            work_location_id: proj.work_location_id ? String(proj.work_location_id) : "",
                            patternTypes,
                            is_remote_enabled: !!proj.is_remote_enabled,
                            remote_type: proj.remote_type || "NONE",
                            remote_frequency_value: proj.remote_frequency_value ?? "",
                        });
                    });
                });
                setRows(newRows);
            }
        } catch (err) {
            console.error("プロジェクトの取得に失敗:", err);
        }
    };

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                const locRes = await api.get("/api/admin/workplaces/");
                setLocationsList(Array.isArray(locRes.data) ? locRes.data : locRes.data?.results || []);
            } catch (err) {
                console.error("データベースを取得できませんでした:", err);
            }
        };
        fetchMasterData();
        loadRows();
    }, []);

    const handleSaveAll = async () => {
        const invalid = rows.find((r) => !r.work_location_id || !r.project_name.trim());
        if (invalid) {
            setMsg("勤務地とプロジェクト名は必須です。");
            return;
        }
        setSaving(true); setMsg("");
        try {
            // 同一プロジェクトの行を1つの ProjectMaster にまとめて保存
            const groups = new Map();
            rows.forEach((r) => {
                const k = groupKey(r);
                if (!groups.has(k)) groups.set(k, []);
                groups.get(k).push(r);
            });

            for (const groupRows of groups.values()) {
                const first = groupRows[0];
                const hasFreq = first.remote_type === "WEEKLY" || first.remote_type === "MONTHLY";
                const isRemote = first.remote_type !== "NONE";
                // タイプはこの画面では編集しないが、既存分を維持して送り返す（消さない）
                const patterns = groupRows.map((r, idx) => ({
                    pattern_number: idx + 1,
                    types: (r.patternTypes || []).map((t, j) => ({
                        type_number: t.type_number || j + 1,
                        shift_id: t.shift_id,
                    })),
                }));
                const payload = {
                    project_name: first.project_name,
                    work_location_id: first.work_location_id,
                    is_remote_enabled: isRemote,
                    remote_type: first.remote_type,
                    remote_frequency_value: hasFreq && first.remote_frequency_value !== ""
                        ? Number(first.remote_frequency_value) : null,
                    patterns,
                };
                if (first.server_id) {
                    await api.put(`/api/admin/projects/${first.server_id}/`, payload);
                } else {
                    await api.post("/api/admin/projects/", payload);
                }
            }
            await loadRows();
            setMsg("保存しました。");
        } catch (err) {
            console.error("保存に失敗:", err);
            const detail = err.response?.data ? JSON.stringify(err.response.data) : "";
            setMsg(`保存に失敗しました。${detail}`);
        } finally {
            setSaving(false);
        }
    };

    const handleBaseChange = (id, field, value) => {
        setRows(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    };

    const handleRemoteChange = (id, field, value) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    };

    // 「パターン追加」＝行（パターン）を1つ追加
    const handleAddPattern = () => {
        setRows([...rows, {
            id: Date.now(),
            project_name: "",
            work_location_id: "",
            patternTypes: [],
            is_remote_enabled: false,
            remote_type: "NONE",
            remote_frequency_value: "",
        }]);
    };

    const handleEditRow = (id) => {
        const target = rows.find(r => r.id === id);
        console.log("プロジェクト修正:", target);
        alert(`プロジェクト「${target.project_name}」の編集モードに移行します(Funtion).`);
    };

    const handleDeleteRow = async (id) => {
        if (!window.confirm("このパターンを削除してもよろしいですか？")) return;
        const target = rows.find((r) => r.id === id);
        const remaining = rows.filter((r) => r.id !== id);
        if (target?.server_id) {
            const stillHasProject = remaining.some((r) => r.server_id === target.server_id);
            if (!stillHasProject) {
                try {
                    await api.delete(`/api/admin/projects/${target.server_id}/`);
                } catch (err) {
                    console.error("削除に失敗:", err);
                    setMsg("削除に失敗しました。");
                    return;
                }
            }
        }
        setRows(remaining);
    };

    return (
        <div className="project-table-container">

        <div className="project-page-header">
            <h2 className="project-page-title"> プロジェクト設定</h2>
        </div>

        <div style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            
                <button type="button" onClick={handleAddPattern} className="wlm-btn-add" style={{ backgroundColor: "#16a34a", color: "white", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", cursor: "pointer" }}>
                    パターン追加
                </button>
                <button type="button" onClick={handleSaveAll} disabled={saving}
                    className="wlm-btn-save"
                    style={{ backgroundColor: "#2563eb", color: "white", border: "none", padding: "6px 16px", borderRadius: "4px", fontWeight: "bold", cursor: saving ? "not-allowed" : "pointer", marginLeft: "8px" }}>
                    {saving ? "保存中…" : "保存"}
                </button>
                <button type="button" className="wlm-btn-back" onClick={() => navigate(-1)} style={{ backgroundColor: "#0099ff", color: "white", border: "none", padding: "6px 16px", borderRadius: "4px", fontWeight: "bold", cursor: "pointer" }}>
                    戻る
                </button>
            </div>
        </div>

        {msg && (
            <div style={{ marginBottom: "8px", textAlign: "right", color: msg.includes("失敗") ? "#dc2626" : "#16a34a", fontWeight: "bold" }}>
                {msg}
            </div>
        )}

        <div className="project-table-scroll-wrapper">
            <table className="project-custom-table">
            <thead>
                <tr>
                <th className="sticky-col-0">パターン</th>
                <th className="sticky-col-1">勤務地</th>
                <th className="sticky-col-2">プロジェクト名</th>
                <th style={{ minWidth: "80px" }}>リモートワーク設定</th>
                <th style={{ minWidth: "84px" }}>操作</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                <tr key={r.id}>
                    <td className="sticky-col-0">{patternNoByRowId[r.id]}</td>

                    <td className="sticky-col-1">
                    <select
                        value={r.work_location_id}
                        onChange={(e) => handleBaseChange(r.id, "work_location_id", e.target.value)}
                        className="table-select font-bold"
                    >
                        <option value="">-- 選択 --</option>
                        {locationsList.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.location_name || loc.name || loc.workplace_name || loc.locationName}</option>
                        ))}
                    </select>
                    </td>

                    <td className="sticky-col-2">
                    <input
                        type="text"
                        value={r.project_name}
                        onChange={(e) => handleBaseChange(r.id, "project_name", e.target.value)}
                        className="table-input-text-name"
                        placeholder="プロジェクト名"
                    />
                    </td>

                    <td className="remote-config-cell">
                        <select
                            className="remote-select table-select"
                            value={r.remote_type}
                            onChange={(e) => {
                                const t = e.target.value;
                                handleRemoteChange(r.id, "remote_type", t);
                                handleRemoteChange(r.id, "is_remote_enabled", t !== "NONE");
                                if (t === "WEEKLY") handleRemoteChange(r.id, "remote_frequency_value", r.remote_frequency_value || "2");
                                else if (t === "MONTHLY") handleRemoteChange(r.id, "remote_frequency_value", r.remote_frequency_value || "3");
                                else handleRemoteChange(r.id, "remote_frequency_value", "");
                            }}
                        >
                            <option value="NONE">なし</option>
                            <option value="ALL">すべて</option>
                            <option value="WEEKLY">週</option>
                            <option value="MONTHLY">月</option>
                        </select>

                        {(r.remote_type === "WEEKLY" || r.remote_type === "MONTHLY") && (
                            <span className="remote-freq-inline">
                                <input
                                    type="number"
                                    min="1"
                                    max={r.remote_type === "WEEKLY" ? "7" : "31"}
                                    value={r.remote_frequency_value}
                                    onChange={(e) => handleRemoteChange(r.id, "remote_frequency_value", e.target.value)}
                                    className="table-input-number-mini"
                                />
                                <span className="unit-text" style={{ color: "#000" }}>回</span>
                            </span>
                        )}
                    </td>
                    <td className="action-buttons-cell">
                    <div className="action-flex-box">
                        <button type="button" className="btn-table-edit" onClick={() => handleEditRow(r.id)}>
                        修正
                        </button>
                        <button type="button" className="btn-table-delete" onClick={() => handleDeleteRow(r.id)}>
                        削除
                        </button>
                    </div>
                    </td>
                </tr>
                ))}
                {rows.length === 0 && (
                    <tr>
                        <td colSpan={5} style={{ textAlign: "center", color: "#888", padding: "12px" }}>
                            「パターン追加」でパターン（勤務地＋プロジェクト名）を追加してください。タイプはパターン表示画面で登録します。
                        </td>
                    </tr>
                )}
            </tbody>
            </table>
        </div>
        </div>
    );
};

export default ProjectMasterTable;
